import type { KeyboardEventHandler } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  useTheme,
} from '@chakra-ui/react';

import { CustomIcon, FeatherIcon } from '../icon';

type Props = {
  value: string;
  isSearchModeActive: boolean;
  onChange: (value: string) => void;
  hasActiveFilters: () => boolean;
  setIsSearchModeActive: (isActive: boolean) => void;
  setSearchValue: (value: string) => void;
  placeholder?: string;
  withFilters?: boolean;
  isFilterActive?: boolean;
  onFilterIconClick?: () => void;
  inputProps?: InputProps;
};

export type SearchInputRefHandle = {
  focus: VoidFunction;
  reset: VoidFunction;
};

const SearchInput = forwardRef<SearchInputRefHandle, Props>(
  (
    {
      value,
      withFilters,
      isFilterActive,
      isSearchModeActive,
      placeholder,
      inputProps,
      onChange,
      onFilterIconClick,
      hasActiveFilters,
      setIsSearchModeActive,
      setSearchValue,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation('shared');
    const [isFocused, setIsFocused] = useState(false);
    const theme = useTheme();

    useEffect(() => () => setIsSearchModeActive(false), [setIsSearchModeActive]);

    const handleClear = () => {
      onChange('');
      setSearchValue('');
      setIsSearchModeActive(false);
    };

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      reset: () => {
        inputRef.current?.blur();
        setIsFocused(false);
        handleClear();
      },
    }));

    const onFocus = () => {
      setIsFocused(true);
      setIsSearchModeActive(true);
    };

    const onBlur = () => {
      setIsFocused(false);
      if (!value) {
        handleClear();
      }
    };

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (e.key === 'Escape') {
        inputRef.current?.blur();
        handleClear();
      }
    };

    return (
      <InputGroup size='lg'>
        <InputLeftElement pointerEvents='none'>
          <FeatherIcon name='search' stroke={isFocused ? 'primary400' : 'neutral500'} />
        </InputLeftElement>
        <Input
          ref={inputRef}
          placeholder={placeholder ?? t('search.placeholder')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          {...inputProps}
        />
        <InputRightElement h={6} top={3} right={3} w='auto'>
          {isSearchModeActive && (
            <IconButton
              size='xs'
              borderRadius='full'
              bg={isFocused ? 'neutral100' : 'neutral200'}
              icon={<CustomIcon name='clear' color='neutral500' size={16} />}
              _focusVisible={{
                boxShadow: theme.shadows.outlinePrimary400,
              }}
              onClick={handleClear}
              aria-label={t('search.disableSearchMode')}
            />
          )}
          {withFilters && (
            <>
              <Box w='1px' bg='neutral300' h={6} mx={2} />
              <IconButton
                onClick={onFilterIconClick}
                size='xs'
                aria-label={hasActiveFilters() ? t('search.clearFilters') : t('search.showFilters')}
                icon={
                  <>
                    {(hasActiveFilters() || isFilterActive) && (
                      <Box
                        pos='absolute'
                        w={2}
                        h={2}
                        bg='secondary400'
                        borderRadius='full'
                        zIndex='banner'
                        top={0}
                        right={0}
                      />
                    )}
                    <FeatherIcon name='sliders' color='neutral600' />
                  </>
                }
                sx={{
                  _focusVisible: {
                    boxShadow: theme.shadows.outlinePrimary400,
                  },
                }}
              />
            </>
          )}
        </InputRightElement>
      </InputGroup>
    );
  },
);

export default SearchInput;
